import { getReviewForProduct } from "@faire/prototype-playground/data/entity_sets/reviewsDatabase";
import type { FaireReviewEntity } from "@faire/playground-entity-db/entity_types/review";
import { getEntityByToken } from "@faire/prototype-playground/data/get_entity";
import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import { PADX } from "../constants";

interface ReviewedProduct {
  token: string;
  name: string;
  imageUrl?: string;
}

interface BrandReviewsProps {
  rating: number;
  reviewCount: number;
  /** Star-rating histogram (5 buckets). Omit to skip the histogram bars. */
  distribution?: { stars: number; count: number }[];
  /** Real brand products with a real per-product review to feature as quote
   * cards (see `getReviewForProduct`) — same sourcing as `HighlyRated`, just a
   * different slice of the pool so the two modules don't repeat a quote. */
  products: ReviewedProduct[];
  /** Real brand-level reviews (see `getBrandReviews`) — a shopper's overall
   * review of the brand, with the multiple products they purchased. Shown
   * ahead of the per-product quote cards in the same card row. */
  brandReviews?: FaireReviewEntity[];
}

interface ReviewCardEntry {
  review: FaireReviewEntity;
  purchased: ReviewedProduct[];
}

/**
 * Simplified reviews module pulled from the latest PDP prototype
 * (PDPRefreshIterationR3): header + left rating/histogram + up to three
 * review cards, all in one row. Brand-level reviews (a shopper's overall
 * take, with everything they purchased) fill the row first; real per-product
 * quote cards (see `getReviewForProduct`) fill any remaining slots — a
 * product with no real review imported yet is skipped rather than shown
 * with one.
 */
export const BrandReviews: React.FC<BrandReviewsProps> = ({
  rating,
  reviewCount,
  distribution,
  products,
  brandReviews = [],
}) => {
  const brandReviewCards: ReviewCardEntry[] = brandReviews.map((review) => ({
    review,
    purchased: (review.purchasedProductTokens ?? [])
      .map((token) => getEntityByToken(token, "product"))
      .filter(
        (product): product is NonNullable<typeof product> =>
          product !== undefined
      ),
  }));
  const productReviewCards: ReviewCardEntry[] = products
    .map((product) => ({ product, review: getReviewForProduct(product.token) }))
    .filter(
      (
        entry
      ): entry is { product: ReviewedProduct; review: FaireReviewEntity } =>
        entry.review !== undefined
    )
    .map(({ product, review }) => ({ review, purchased: [product] }));
  const reviews = [...brandReviewCards, ...productReviewCards].slice(0, 3);
  const total = (distribution ?? []).reduce(
    (sum, bucket) => sum + bucket.count,
    0
  );

  return (
    <div className={cn("pt-[88px] pb-16", PADX)}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="type-fs-section-heading text-fs-text-primary">
          Rating &amp; Reviews
        </h2>
        <button className="type-fs-paragraph text-fs-text-primary underline">
          See all {reviewCount} reviews
        </button>
      </div>

      <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-x-[var(--grid-gutter,16px)]">
        {/* Left: rating + histogram */}
        <div className="flex w-full flex-shrink-0 flex-col gap-6 md:w-[301px]">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                className="text-fs-text-primary"
                style={{ fontSize: "30px", lineHeight: 1 }}
              >
                {rating.toFixed(1)}
              </span>
              <Star />
            </div>
            <p className="type-fs-paragraph text-fs-text-subdued">
              <button className="text-fs-text-primary underline">
                {reviewCount} reviews
              </button>
            </p>
          </div>

          {distribution && distribution.length > 0 ? (
            <div className="flex flex-col gap-1">
              {distribution.map((bucket) => (
                <div key={bucket.stars} className="flex h-3 items-center gap-3">
                  <span className="type-fs-label text-fs-text-primary w-2.5 text-right">
                    {bucket.stars}
                  </span>
                  <div
                    className="h-1 w-[187px] flex-shrink-0 overflow-hidden rounded-full"
                    style={{ backgroundColor: "#dfe0e1" }}
                  >
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.round((bucket.count / total) * 100)}%`,
                        backgroundColor: "#333333",
                      }}
                    />
                  </div>
                  <span className="type-fs-label text-fs-text-subdued">
                    {bucket.count}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Right: up to three review cards — brand-level reviews first, then
            per-product quote cards, all in one row. */}
        <div className="flex flex-1 flex-col gap-[var(--grid-gutter,16px)] sm:flex-row">
          {reviews.map(({ review, purchased }) => (
            <ReviewCard
              key={review.token}
              review={review}
              purchased={purchased}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const STAR_PATH =
  "m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z";

const Star: React.FC<{ filled?: boolean }> = ({ filled = true }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={filled ? "#333333" : "#e5e5e5"}
    aria-hidden
  >
    <path d={STAR_PATH} />
  </svg>
);

const ReviewCard: React.FC<{
  review: FaireReviewEntity;
  purchased: ReviewedProduct[];
}> = ({ review, purchased }) => (
  <div className="border-fs-border-muted flex min-w-0 flex-1 flex-col gap-3 rounded-md border p-4 md:p-6">
    <div className="flex items-start justify-between">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} filled={star <= review.stars} />
        ))}
      </div>
      <span className="type-fs-label text-fs-text-subdued text-right">
        {review.reviewDate}
      </span>
    </div>
    <div className="flex flex-col gap-1">
      <p className="type-fs-paragraph text-fs-text-primary">
        {review.reviewText}
      </p>
      <p className="type-fs-label text-fs-text-subdued">
        {review.reviewerName}
        {review.reviewerLocation ? ` · ${review.reviewerLocation}` : ""}
      </p>
    </div>
    {purchased.length > 0 ? (
      <div className="mt-auto flex flex-col gap-2 pt-2">
        <span className="type-fs-label text-fs-text-subdued">Purchased</span>
        <div className="flex gap-2.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {purchased.map((product) => (
            <div
              key={product.token}
              title={product.name}
              className="bg-fs-surface-secondary h-16 w-16 flex-shrink-0 overflow-hidden rounded"
            >
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);
